"use client";

import { useState, useRef, useEffect } from "react";
import type { FaqBlock, ResourceItem } from "@/lib/content";
import { resourceHasLink } from "@/lib/editable";

function ResourceRow({ item }: { item: ResourceItem }) {
  // The "↗" promises a new tab. An Uploaded Document is site-relative, so the
  // http test alone would open the PDF over the page it was cited from. Keyed
  // on the href, not the PDF/Link tag, so an anchor or a page on this site
  // still opens in place whatever the editor tagged it.
  const newTab = /^https?:\/\//i.test(item.href) || item.href.startsWith("/documents/");
  return (
    <a
      href={item.href}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noopener noreferrer" : undefined}
      className="flex items-center justify-between gap-4 border-b border-line py-3 text-[15px] transition-colors hover:text-brass-deep"
    >
      <span className="font-medium">{item.label}</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-60">
        {item.kind === "pdf" ? "PDF" : "Link"} ↗
      </span>
    </a>
  );
}

export function FaqItem({ item }: { item: FaqBlock["items"][number] }) {
  // A row still waiting for its link is the editor's to-do, not a visitor's
  // dead end (ADR-0021).
  const resources = (item.resources ?? []).filter(resourceHasLink);
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(open ? undefined : 0);

  useEffect(() => {
    if (open) {
      const el = contentRef.current;
      if (el) {
        setHeight(el.scrollHeight);
      }
    } else {
      setHeight(0);
    }
  }, [open]);

  return (
    <div className="group border-b border-line">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left outline-none"
      >
        <span className="font-display text-[19px] font-semibold text-ink">
          {item.question}
        </span>
        <span
          className={`font-mono text-brass-deep transition-transform duration-300 ${
            open ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>
      <div
        className="overflow-hidden transition-[height] duration-300 ease-in-out"
        style={{ height }}
      >
        <div ref={contentRef} className="pb-6">
          <div
            className="prose-editorial max-w-prose2 text-[16px] leading-[1.7] text-ink-60"
            dangerouslySetInnerHTML={{ __html: item.answerHtml }}
          />
          {resources.length > 0 && (
            <div className="mt-5 max-w-prose2">
              {resources.map((r, ri) => (
                <ResourceRow key={`${r.href}-${r.label}-${ri}`} item={r} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
